
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AuthProvider } from "./contexts/AuthContext";
import { UnauthorizedAccess } from "./components/auth/UnauthorizedAccess";
import { useAuth } from "./contexts/AuthContext";

// Pages
import Index from "./pages/Index";
import Admin from "./pages/admin";
import Courses from "./pages/members/Courses";
import CoursesVideos from "./pages/members/CoursesVideos";
import CoursesInteractive from "./pages/members/CoursesInteractive";
import CoursesWebinars from "./pages/members/CoursesWebinars";
import Resources from "./pages/members/Resources";
import ResourcesDocuments from "./pages/members/ResourcesDocuments";
import ResourcesCode from "./pages/members/ResourcesCode";
import ResourcesVideos from "./pages/members/ResourcesVideos";
import ResourcesGuides from "./pages/members/ResourcesGuides";
import QA from "./pages/members/QA";
import QAForum from "./pages/members/QAForum";
import QAQuestions from "./pages/members/QAQuestions";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Προστατευμένη διαδρομή που ελέγχει αν ο χρήστης είναι συνδεδεμένος
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  
  // Αν ο χρήστης δεν είναι συνδεδεμένος, εμφάνισε την σελίδα μη εξουσιοδοτημένης πρόσβασης
  if (!isAuthenticated) {
    return <UnauthorizedAccess />;
  }
  
  return <>{children}</>;
}

// Προστατευμένη διαδρομή ειδικά για το Admin panel
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin } = useAuth();
  
  // Αν ο χρήστης δεν είναι συνδεδεμένος ή δεν είναι admin
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      
      {/* Admin routes - απαιτείται πρόσβαση διαχειριστή */}
      <Route path="/admin/*" element={<AdminRoute><Admin /></AdminRoute>} />
      
      {/* Courses routes - απαιτείται σύνδεση */}
      <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
      <Route path="/courses/videos" element={<ProtectedRoute><CoursesVideos /></ProtectedRoute>} />
      <Route path="/courses/interactive" element={<ProtectedRoute><CoursesInteractive /></ProtectedRoute>} />
      <Route path="/courses/webinars" element={<ProtectedRoute><CoursesWebinars /></ProtectedRoute>} />
      
      {/* Resources routes - απαιτείται σύνδεση */}
      <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
      <Route path="/resources/documents" element={<ProtectedRoute><ResourcesDocuments /></ProtectedRoute>} />
      <Route path="/resources/code" element={<ProtectedRoute><ResourcesCode /></ProtectedRoute>} />
      <Route path="/resources/videos" element={<ProtectedRoute><ResourcesVideos /></ProtectedRoute>} />
      <Route path="/resources/guides" element={<ProtectedRoute><ResourcesGuides /></ProtectedRoute>} />
      
      {/* Q&A routes - απαιτείται σύνδεση */}
      <Route path="/qa" element={<ProtectedRoute><QA /></ProtectedRoute>} />
      <Route path="/qa/forum" element={<ProtectedRoute><QAForum /></ProtectedRoute>} />
      <Route path="/qa/questions" element={<ProtectedRoute><QAQuestions /></ProtectedRoute>} />
      
      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <NotificationProvider>
            <AppRoutes />
            <Toaster />
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
